import React, { useEffect, useMemo, useCallback } from "react"
import { Box, Typography } from "@carrotfertility/carotene-core"
import { FormAlert, FormSelect, useFormContext } from "@carrotfertility/carotene-core-x"
import { useWatch } from "react-hook-form"
import { Journeys } from "../../types/Journeys"
import { CarrotPlanErrorMessage, FieldName } from "./CarrotPlanFormTypes"
import { useResetDependentFieldsOnChange } from "./hooks/useResetDependentFieldsOnChange"

interface JourneySelectionProps {
  journeysWithStages: Journeys
  employeeName: string
  inReview: boolean
}

interface SelectOption {
  label: string
  value: number
}

export const NONE_VALUE = 0
export const NONE_OPTION = { label: "None", value: NONE_VALUE }
export const SOMETHING_ELSE_ENUM = "SOMETHING_ELSE"

const JourneySelectRow = ({
  orderName,
  journeyFieldName,
  journeyOptions,
  journeyPhaseFieldName,
  journeysWithStages,
  inReview
}: {
  journeyFieldName: FieldName
  journeyOptions: SelectOption[]
  journeyPhaseFieldName: FieldName
  orderName: string
  journeysWithStages: Journeys
  inReview: boolean
}) => {
  const capitalizeFirstLetter = (str) => {
    if (str.length === 0) {
      return ""
    }
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  const journeyValue = useWatch({ name: journeyFieldName })
  const capitalizedOrderName = capitalizeFirstLetter(orderName)

  const getJourneyStagesForJourney = useCallback(
    (journeyId: number): SelectOption[] => {
      if (!journeyId || journeyId === NONE_VALUE) {
        return [NONE_OPTION]
      }

      const journey = journeysWithStages.find((journey) => journey.journeyId === journeyId)
      if (!journey) {
        return [NONE_OPTION]
      }

      return [
        NONE_OPTION,
        ...journey.stages.map((stage) => ({
          label: stage.journeyStageShortName,
          value: stage.journeyStageId
        }))
      ]
    },
    [journeysWithStages]
  )

  const primaryJourneyValidation = (value) => value !== NONE_VALUE || "Select a journey"
  const primaryJourneyPhaseValidation = (value) => value !== NONE_VALUE || "Select a phase"

  const otherJourneyPhaseValidation = (value) => {
    if (
      (value === NONE_VALUE && journeyValue === NONE_VALUE) ||
      (journeyValue !== NONE_VALUE && value !== NONE_VALUE)
    ) {
      return true
    }
    return `${capitalizedOrderName} journey phase required if ${orderName} journey is selected`
  }

  return (
    <Box flexDirection="row" gap={(theme) => theme.spacing(theme.tokens.spacing.lg)} display="flex" width="100%">
      <Box flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.md)} display="flex" flex="1">
        <FormSelect
          name={journeyFieldName}
          label={`${capitalizedOrderName} Journey`}
          placeholder={`Select ${orderName} journey`}
          options={journeyOptions}
          data-testid={`${orderName}-journey-select`}
          disabled={inReview}
          controllerProps={{
            rules: {
              required: orderName === "primary",
              validate: orderName === "primary" ? primaryJourneyValidation : null
            }
          }}
        />
      </Box>
      <Box flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.md)} display="flex" flex="1">
        <FormSelect
          name={journeyPhaseFieldName}
          label={`${capitalizedOrderName} Journey Phase`}
          placeholder={`Select ${orderName} journey phase`}
          options={getJourneyStagesForJourney(journeyValue)}
          data-testid={`${orderName}-journey-phase-select`}
          disabled={inReview}
          controllerProps={{
            rules: {
              required: orderName === "primary",
              validate: orderName === "primary" ? primaryJourneyPhaseValidation : otherJourneyPhaseValidation
            }
          }}
        />
      </Box>
    </Box>
  )
}

export const JourneySelectSection: React.FC<JourneySelectionProps> = ({
  journeysWithStages,
  employeeName,
  inReview
}) => {
  const {
    setValue,
    trigger,
    formState: { errors, isSubmitted }
  } = useFormContext()

  const journeySelectOptions: SelectOption[] = useMemo(
    () => [
      NONE_OPTION,
      ...journeysWithStages
        .filter((journey) => journey.journeyEnumName !== SOMETHING_ELSE_ENUM)
        .map((journey) => ({
          label: journey.journeyDisplayName,
          value: journey.journeyId
        }))
    ],
    [journeysWithStages]
  )

  const primaryJourney = useWatch({ name: FieldName.primaryJourney })
  const primaryJourneyPhase = useWatch({ name: FieldName.primaryJourneyPhase })
  const secondaryJourney = useWatch({ name: FieldName.secondaryJourney })
  const secondaryJourneyPhase = useWatch({ name: FieldName.secondaryJourneyPhase })
  const tertiaryJourney = useWatch({ name: FieldName.tertiaryJourney })

  const shouldShowSecondary = primaryJourney !== NONE_VALUE && primaryJourneyPhase !== NONE_VALUE
  const shouldShowTertiary =
    shouldShowSecondary && secondaryJourney !== NONE_VALUE && secondaryJourneyPhase !== NONE_VALUE

  useResetDependentFieldsOnChange(primaryJourney, setValue, () => {
    setValue(FieldName.primaryJourneyPhase, NONE_VALUE)
    setValue(FieldName.secondaryJourney, NONE_VALUE)
  })

  useEffect(() => {
    setValue(FieldName.secondaryJourneyPhase, NONE_VALUE)
    setValue(FieldName.tertiaryJourney, NONE_VALUE)
    if (isSubmitted) {
      trigger(FieldName.secondaryJourneyPhase)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondaryJourney, setValue, trigger])

  useEffect(() => {
    setValue(FieldName.tertiaryJourneyPhase, NONE_VALUE)
    if (isSubmitted) {
      trigger(FieldName.tertiaryJourneyPhase)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tertiaryJourney, setValue, trigger])

  // Memoized filtered options for secondary and tertiary journeys
  const secondaryJourneyOptions = useMemo(
    () => journeySelectOptions.filter((journey) => journey.value !== primaryJourney),
    [journeySelectOptions, primaryJourney]
  )

  const tertiaryJourneyOptions = useMemo(
    () =>
      journeySelectOptions.filter((journey) => journey.value !== primaryJourney && journey.value !== secondaryJourney),
    [journeySelectOptions, primaryJourney, secondaryJourney]
  )

  return (
    <Box flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.lg)} display="flex" width={"992px"}>
      <Box flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.md)} display="flex" width="100%">
        <Typography variant="h4">{employeeName}&apos;s Journey</Typography>
        {(errors[FieldName.primaryJourney] || errors[FieldName.primaryJourneyPhase]) && (
          <FormAlert severity="error">{CarrotPlanErrorMessage.NEEDS_JOURNEY_OR_PHASE}</FormAlert>
        )}
      </Box>
      <JourneySelectRow
        orderName="primary"
        journeyFieldName={FieldName.primaryJourney}
        journeyOptions={journeySelectOptions}
        journeyPhaseFieldName={FieldName.primaryJourneyPhase}
        journeysWithStages={journeysWithStages}
        inReview={inReview}
      />
      {shouldShowSecondary && (
        <JourneySelectRow
          orderName="secondary"
          journeyFieldName={FieldName.secondaryJourney}
          journeyOptions={secondaryJourneyOptions}
          journeyPhaseFieldName={FieldName.secondaryJourneyPhase}
          journeysWithStages={journeysWithStages}
          inReview={inReview}
        />
      )}
      {shouldShowTertiary && (
        <JourneySelectRow
          orderName="tertiary"
          journeyFieldName={FieldName.tertiaryJourney}
          journeyOptions={tertiaryJourneyOptions}
          journeyPhaseFieldName={FieldName.tertiaryJourneyPhase}
          journeysWithStages={journeysWithStages}
          inReview={inReview}
        />
      )}
    </Box>
  )
}
